import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Live show state over WebSocket. The server pushes a full `snapshot` whenever
 * anything about the show changes (program, participants, jobs). Auto-reconnects.
 *
 * auth: { token } (producer session) | { programKey } (program output page)
 *       | { participantId, joinKey } (guest / camera)
 */
export function useShowSocket(showId, auth, onSnapshot, onEvent) {
  const [connected, setConnected] = useState(false);
  const [show, setShow] = useState(null);
  const wsRef = useRef(null);
  const timerRef = useRef(null);
  const lastMsgRef = useRef(Date.now());
  const closedRef = useRef(false);
  const cbRef = useRef(onSnapshot);
  cbRef.current = onSnapshot;
  const evRef = useRef(onEvent);
  evRef.current = onEvent;
  const authKey = JSON.stringify(auth || {});

  const connect = useCallback(() => {
    if (!showId) return;
    const a = JSON.parse(authKey);
    const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
    const params = new URLSearchParams();
    if (a.token) params.set("token", a.token);
    if (a.programKey) params.set("k", a.programKey);
    if (a.monitorKey) params.set("m", a.monitorKey);
    if (a.participantId) { params.set("p", a.participantId); params.set("key", a.joinKey || ""); }
    let ws;
    try {
      ws = new WebSocket(`${base}/api/ws/show/${showId}?${params.toString()}`);
    } catch (e) {
      return;
    }
    wsRef.current = ws;
    lastMsgRef.current = Date.now();
    ws.onopen = () => { setConnected(true); lastMsgRef.current = Date.now(); };
    ws.onmessage = (ev) => {
      lastMsgRef.current = Date.now();
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "snapshot" && msg.show) {
          setShow(msg.show);
          if (cbRef.current) cbRef.current(msg.show);
        } else if (msg.type && msg.type !== "pong" && evRef.current) {
          evRef.current(msg);
        }
      } catch (e) { /* ignore */ }
    };
    ws.onclose = () => {
      setConnected(false);
      if (!closedRef.current) timerRef.current = setTimeout(connect, 2000);
    };
    ws.onerror = () => { try { ws.close(); } catch (e) { /* ignore */ } };
  }, [showId, authKey]);

  useEffect(() => {
    closedRef.current = false;
    connect();
    // Heartbeat + half-open watchdog: a laptop that slept or changed Wi‑Fi can keep a socket
    // that never closes and never delivers again. No pong/snapshot for 70 s -> force a reconnect.
    const hb = setInterval(() => {
      const ws = wsRef.current;
      if (!ws) return;
      if (ws.readyState === 1 && Date.now() - lastMsgRef.current > 70000) { try { ws.close(); } catch (e) { /* ignore */ } return; }
      if (ws.readyState === 1) ws.send(JSON.stringify({ type: "ping" }));
    }, 25000);
    const kick = () => {
      const ws = wsRef.current;
      if (!ws || ws.readyState === 1 || ws.readyState === 0) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      connect();
    };
    window.addEventListener("online", kick);
    document.addEventListener("visibilitychange", kick);
    return () => {
      closedRef.current = true;
      clearInterval(hb);
      window.removeEventListener("online", kick);
      document.removeEventListener("visibilitychange", kick);
      if (timerRef.current) clearTimeout(timerRef.current);
      try { wsRef.current?.close(); } catch (e) { /* ignore */ }
    };
  }, [connect]);

  return { show, setShow, connected };
}

export default useShowSocket;
